/**
 * PanelNumberRow — one numeric parameter, on one line: title, range, slider, range, typed value.
 *
 *     MOUNTAIN HEIGHT   200 ──────●──────── 16000  [ 4000 ] vox
 *     Peak height above sea level.
 *
 * THE ENDS OF THE RANGE ARE ON THE ENDS OF THE SLIDER. Without them a handle two-thirds along says
 * nothing — two-thirds of what? — and the readout alone cannot tell you whether 4000 is near the top
 * of what the world will accept or nowhere near it. They were the one thing the old row left the
 * user to guess.
 *
 * THE TITLE COLUMN WRAPS RATHER THAN TRUNCATES. "Partition thickness" does not fit 92 px on one
 * line at any size worth reading, and a title clipped to "PARTITION THICK…" is the complaint this
 * row exists to answer. Two short lines cost a few pixels of height and nothing else.
 *
 * IT IS ALSO THE ONLY PART THAT SHRINKS. Five things on one line is a tight budget, and it gets
 * tighter with depth — Stairs sits six levels in. The bounds and the value box hold their size
 * because a clipped number is worse than useless; the title takes the squeeze and wraps another
 * line, so the row never pushes the sheet into a horizontal scroll.
 *
 * PRECISION FOLLOWS THE STEP. A 0.0025 step formatted to two decimals reads "0.00" for its whole
 * range — the number stops moving while the handle does, which reads as a broken control.
 */

import { PanelSlider } from './PanelSlider';
import { PanelNumberField } from './PanelNumberField';
import { MOTION, PARAM_BOUND_W, PARAM_TITLE_W, ROW_LABEL } from './tokens';

interface PanelNumberRowProps {
  label: string;
  desc?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  /** Appended to the readout ('m', '%', 'vox'). */
  unit?: string;
  disabled?: boolean;
  title?: string;
}

/**
 * Decimals a step needs to stay legible: exactly as many as the step itself carries.
 *
 * The rule used to be a pair of thresholds (`step < 0.01 ? 4 : 2`), which reads "0.0050" and "0.75"
 * correctly and "25.00" for a step of 2.5 — two dead digits on every region-irregularity row. Taking
 * the count from the step handles every case the thresholds did, and that one.
 */
export function formatForStep(value: number, step: number): string {
  const dot = String(step).indexOf('.');
  return value.toFixed(dot < 0 ? 0 : String(step).length - dot - 1);
}

/** An end of the range. A FIXED column, so every track in the sheet starts and stops on one x. */
function Bound({ value, step, align }: { value: number; step: number; align: 'right' | 'left' }) {
  return (
    <span className={`${PARAM_BOUND_W} shrink-0 text-[9px] font-semibold tabular-nums
      text-neutral-300 ${align === 'right' ? 'text-right' : 'text-left'}`}>
      {formatForStep(value, step)}
    </span>
  );
}

export function PanelNumberRow({
  label, desc, value, min, max, step, onChange, unit, disabled, title,
}: PanelNumberRowProps) {
  return (
    <div className={`${MOTION} ${disabled ? 'opacity-40 pointer-events-none' : ''}`} title={title}>
      <div className="flex items-center gap-1.5">
        <div className={`${ROW_LABEL} !whitespace-normal !overflow-visible ${PARAM_TITLE_W} leading-[1.15]`}>
          {label}
        </div>
        <Bound value={min} step={step} align="right" />
        <div className="flex-1 min-w-[40px]">
          <PanelSlider min={min} max={max} step={step} value={value} onChange={onChange} ariaLabel={label} />
        </div>
        <Bound value={max} step={step} align="left" />
        <PanelNumberField
          value={value} min={min} max={max} step={step}
          onChange={onChange} unit={unit} disabled={disabled} ariaLabel={`${label} value`}
        />
      </div>
      {desc && <p className="text-[10px] leading-tight text-neutral-400 pl-[98px] -mt-0.5 pb-1.5">{desc}</p>}
    </div>
  );
}
